import { useRef, useEffect, useCallback, ReactElement } from 'react';
import Modal from '@sdk/components/ui/modal/Modal';
import Row from '@sdk/components/ui/spacing/Row';
import useModalState from '@sdk/components/ui/modal/useModalState';
import { ThemeColorType } from '@sdk/theme/ThemeColorType';
import { useClickOutside } from '@sdk/common/hooks/useClickOutside';
import Text16 from 'sling-sdk/src/components/ui/typography/Text16';
import Text14 from 'sling-sdk/src/components/ui/typography/Text14';

import {
  ConfirmationStateWrapper,
  ModalContainer,
} from '@web/timeClock/ConfirmationStateModal/confirmationStateModal.styles';
import { MODAL_CONFIRMATION_STATE_DELAY } from '@web/timeClock/ConfirmationStateModal/conformationStateModal.consts';

type Props = {
  icon: ReactElement;
  title: string;
  subtitle: string;
  isConfirmationStateVisible: boolean;
  onConfirmationClose: () => void;
};

const ConfirmationStateModal = ({
  icon,
  title,
  subtitle,
  isConfirmationStateVisible,
  onConfirmationClose,
}: Props) => {
  const config = useRef({ noDismiss: true }).current;
  const { isVisible, open, close } = useModalState();
  const ref = useClickOutside<HTMLDivElement>(() => onClose());

  const onClose = useCallback(() => {
    close();
    onConfirmationClose();
  }, [close, onConfirmationClose]);

  useEffect(() => {
    let timeoutId;
    if (isConfirmationStateVisible) {
      open();
      timeoutId = setTimeout(onClose, MODAL_CONFIRMATION_STATE_DELAY);
    }

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [isConfirmationStateVisible, onClose, open]);

  return (
    <ConfirmationStateWrapper>
      <Modal
        fixed
        config={config}
        isVisible={isVisible}
        closePositionTop={13}
        closePositionRight={13}>
        <ModalContainer ref={ref} justifyContent="center" alignItems="center" onClick={onClose}>
          <Row mBottom={22}>{icon}</Row>
          <Row mBottom={5}>
            <Text16 semiBold themeColor={ThemeColorType.gray}>
              {title}
            </Text16>
          </Row>
          <Row>
            <Text14 semiBold themeColor={ThemeColorType.iconGray}>
              {subtitle}
            </Text14>
          </Row>
        </ModalContainer>
      </Modal>
    </ConfirmationStateWrapper>
  );
};

export default ConfirmationStateModal;
